import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Plus,
  Trash2,
  Wrench,
  Upload,
  Star,
  ChevronRight,
  ChevronLeft,
  X,
  Scale,
  Recycle,
  Brain,
  HardHat,
  PanelTop,
  Droplets,
  FolderOpen,
  Bath,
  Save,
} from "lucide-react";
import { API } from "./Sidebar";

export const BuilderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [draftId, setDraftId] = useState(searchParams.get("draft") || null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    template: "",
    business_name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    tagline: "",
    description: "",
    domain_name: "",
    services: [],
    cities: [],
    testimonials: [],
    primary_color: "#ea580c",
    secondary_color: "#0f172a",
    hero_form: false,
    whatsapp: "",
    google_maps_embed: "",
    disclaimer: "",
    near_me_page: true,
    blog_enabled: false,
    blog_articles: [],
    head_scripts: "",
    body_scripts: "",
    site_role: "direct_provider",
    network_name: "",
    service_area_business: false,
    service_area_suffix: "",
  });
  const [newService, setNewService] = useState({ name: "", slug: "", description: "", features: [] });
  const [newCity, setNewCity] = useState({ name: "", slug: "", state: "", suffix: "", description: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [pageImages, setPageImages] = useState({});
  const [pageImagePreviews, setPageImagePreviews] = useState({});

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${API}/templates`);
        setTemplates(res.data.templates);
      } catch (e) {
        toast.error("Failed to load templates");
      }
    };
    fetchTemplates();
  }, []);

  // Load draft if resuming
  useEffect(() => {
    if (!draftId) return;
    const loadDraft = async () => {
      try {
        const res = await axios.get(`${API}/projects/${draftId}`);
        const p = res.data;
        if (p.status !== "draft") {
          toast.info("This project is already published");
          navigate(`/admin/projects/${draftId}`);
          return;
        }
        setFormData({
          name: p.name || "",
          template: p.template || "",
          business_name: p.business_name || "",
          phone: p.phone || "",
          email: p.email || "",
          address: p.address || "",
          city: p.city || "",
          state: p.state || "",
          zip_code: p.zip_code || "",
          tagline: p.tagline || "",
          description: p.description || "",
          domain_name: p.domain_name || "",
          services: p.services || [],
          cities: p.cities || [],
          testimonials: p.testimonials || [],
          primary_color: p.primary_color || "#ea580c",
          secondary_color: p.secondary_color || "#0f172a",
          hero_form: p.hero_form || false,
          whatsapp: p.whatsapp || "",
          google_maps_embed: p.google_maps_embed || "",
          disclaimer: p.disclaimer || "",
          near_me_page: p.near_me_page !== false,
          blog_enabled: p.blog_enabled || false,
          blog_articles: p.blog_articles || [],
          head_scripts: p.head_scripts || "",
          body_scripts: p.body_scripts || "",
        });
        setStep(p.wizard_step || 1);
        toast.success("Draft loaded — continue where you left off");
      } catch (e) {
        toast.error("Failed to load draft");
        setDraftId(null);
      }
    };
    loadDraft();
  }, [draftId]);

  const saveDraft = async () => {
    setIsSavingDraft(true);
    try {
      if (draftId) {
        await axios.put(`${API}/projects/${draftId}/draft`, { ...formData, wizard_step: step });
        toast.success("Draft saved");
      } else {
        const res = await axios.post(`${API}/projects/draft`, { ...formData, wizard_step: step });
        setDraftId(res.data.id);
        // Update URL without reload
        window.history.replaceState(null, "", `/admin/builder?draft=${res.data.id}`);
        toast.success("Draft saved");
      }
    } catch (e) {
      toast.error("Failed to save draft");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const selectTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        ...formData,
        template: templateId,
        primary_color: template.primary_color,
        secondary_color: template.secondary_color,
        services: template.default_services.map(s => ({
          ...s,
          description: `Professional ${s.name.toLowerCase()} services for your home.`,
          features: ["Licensed professionals", "Quality materials", "Satisfaction guaranteed", "Free estimates"]
        })),
      });
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const addService = () => {
    if (!newService.name) return;
    setFormData({
      ...formData,
      services: [...formData.services, {
        ...newService,
        slug: newService.slug || generateSlug(newService.name),
        description: newService.description || `Professional ${newService.name.toLowerCase()} services.`,
        features: newService.features.length ? newService.features : ["Licensed professionals", "Quality materials", "Free estimates"]
      }]
    });
    setNewService({ name: "", slug: "", description: "", features: [] });
  };

  const removeService = (index) => {
    setFormData({
      ...formData,
      services: formData.services.filter((_, i) => i !== index)
    });
  };

  const addCity = () => {
    if (!newCity.name || !newCity.state) return;
    const autoSlug = generateSlug([newCity.name, newCity.state, newCity.suffix].filter(Boolean).join(' '));
    setFormData({
      ...formData,
      cities: [...formData.cities, {
        ...newCity,
        slug: autoSlug,
      }]
    });
    setNewCity({ name: "", slug: "", state: "", suffix: "", description: "" });
  };

  const removeCity = (index) => {
    setFormData({
      ...formData,
      cities: formData.cities.filter((_, i) => i !== index)
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG recommended)");
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let projectId;
      if (draftId) {
        // Update draft with final data then publish
        await axios.put(`${API}/projects/${draftId}/draft`, { ...formData, wizard_step: 6 });
        await axios.post(`${API}/projects/${draftId}/publish-draft`);
        projectId = draftId;
      } else {
        const res = await axios.post(`${API}/projects`, formData);
        projectId = res.data.id;
      }

      // Upload logo if selected
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await axios.post(`${API}/projects/${projectId}/upload-logo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // Upload page images if selected
      for (const [slot, file] of Object.entries(pageImages)) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/projects/${projectId}/upload-image?slot=${slot}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // Upload favicon if selected
      if (faviconFile) {
        const fd = new FormData();
        fd.append("file", faviconFile);
        await axios.post(`${API}/projects/${projectId}/upload-favicon`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast.success("Project created successfully!");
      navigate(`/admin/projects/${projectId}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create project");
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { num: 1, label: "Template" },
    { num: 2, label: "Business Info" },
    { num: 3, label: "Services" },
    { num: 4, label: "Locations" },
    { num: 5, label: "Testimonials" },
    { num: 6, label: "Review" },
  ];

  return (
    <div data-testid="builder-page">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
          Website Builder
        </h2>
        <p className="text-slate-600 mt-1">Create a new lead generation website</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8 max-w-2xl">
        {steps.map((s, index) => (
          <div key={s.num} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= s.num
                  ? "bg-orange-500 text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              {s.num}
            </div>
            <span className={`ml-2 text-sm font-medium ${step >= s.num ? "text-slate-900" : "text-slate-400"}`}>
              {s.label}
            </span>
            {index < steps.length - 1 && (
              <div className={`w-12 h-0.5 mx-4 ${step > s.num ? "bg-orange-500" : "bg-slate-200"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Template Selection */}
      {step === 1 && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold">Choose a Template</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {templates.map((template) => (
              <Card
                key={template.id}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  formData.template === template.id ? "ring-2 ring-orange-500 border-orange-500" : ""
                }`}
                onClick={() => selectTemplate(template.id)}
                data-testid={`template-${template.id}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      {template.id === 'personal-injury'
                        ? <Scale className="w-5 h-5" style={{ color: template.primary_color }} />
                        : template.id === 'scrap-metal'
                        ? <Recycle className="w-5 h-5" style={{ color: template.primary_color }} />
                        : template.id === 'hypnosis'
                        ? <Brain className="w-5 h-5" style={{ color: template.primary_color }} />
                        : template.id === 'couvreur-premium'
                        ? <HardHat className="w-5 h-5" style={{ color: template.primary_color }} />
                        : template.id === 'siding'
                        ? <PanelTop className="w-5 h-5" style={{ color: template.primary_color }} />
                        : template.id === 'water-damage'
                        ? <Droplets className="w-5 h-5" style={{ color: template.primary_color }} />
                        : template.id === 'bathroom-remodel'
                        ? <Bath className="w-5 h-5" style={{ color: template.primary_color }} />
                        : template.id === 'admin-gestion-fr' || template.id === 'admin-gestion-premium'
                        ? <FolderOpen className="w-5 h-5" style={{ color: template.primary_color }} />
                        : <Wrench className="w-5 h-5" style={{ color: template.primary_color }} />
                      }
                      {template.name}
                    </CardTitle>
                    <div className="flex gap-2">
                      <div className="w-6 h-6 rounded-full" style={{ background: template.primary_color }} />
                      <div className="w-6 h-6 rounded-full" style={{ background: template.secondary_color }} />
                    </div>
                  </div>
                  <CardDescription>{template.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-2">Default services:</p>
                  <div className="flex flex-wrap gap-2">
                    {template.default_services.map((s, i) => (
                      <Badge key={i} variant="secondary">{s.name}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex justify-between">
            <Button variant="ghost" onClick={saveDraft} disabled={isSavingDraft || !formData.template} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" /> {isSavingDraft ? "Saving..." : "Save as Draft"}
            </Button>
            <Button onClick={() => setStep(2)} disabled={!formData.template} className="bg-orange-500 hover:bg-orange-600">
              Next <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Business Information */}
      {step === 2 && (
        <div className="space-y-6 max-w-2xl">
          <h3 className="text-lg font-semibold">Business Information</h3>
          {/* Site type — preliminary positioning question that pre-configures
              site_role + service_area_business toggles in one pick. */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
            <Label className="text-base font-semibold text-slate-800">Site type *</Label>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Choose the profile that best describes this site. We'll pre-configure the rank-and-rent toggles for you (you can still tweak them later in Settings).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                {
                  key: 'direct',
                  title: 'Direct business',
                  desc: 'The site IS the contractor and has a real physical office/storefront.',
                  vals: { site_role: 'direct_provider', service_area_business: false },
                },
                {
                  key: 'service_area',
                  title: 'Service-area business',
                  desc: 'Real contractor, no public storefront — services delivered on customers\' sites.',
                  vals: { site_role: 'direct_provider', service_area_business: true },
                },
                {
                  key: 'lead_network',
                  title: 'Lead network (rank-and-rent)',
                  desc: 'Site connects visitors to independent local pros. We are NOT the provider.',
                  vals: { site_role: 'lead_network', service_area_business: true },
                },
                {
                  key: 'hybrid',
                  title: 'Hybrid',
                  desc: 'Network model + a real office address (referral platform with showroom).',
                  vals: { site_role: 'lead_network', service_area_business: false },
                },
              ].map((opt) => {
                const isActive =
                  formData.site_role === opt.vals.site_role &&
                  !!formData.service_area_business === !!opt.vals.service_area_business;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setFormData({ ...formData, ...opt.vals })}
                    className={`text-left p-3 rounded-md border-2 transition-colors ${
                      isActive
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`site-type-${opt.key}`}
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className={`mt-1 w-3 h-3 rounded-full flex-shrink-0 ${
                          isActive ? 'bg-orange-500' : 'bg-slate-300'
                        }`}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-800">{opt.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{opt.desc}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid gap-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Project Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="My Plumbing Website" data-testid="input-project-name" /></div>
              <div><Label>Business Name *</Label><Input value={formData.business_name} onChange={(e) => setFormData({ ...formData, business_name: e.target.value })} placeholder="ABC Plumbing Services" data-testid="input-business-name" /></div>
            </div>
            <div>
              <Label>Website Domain <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
              <Input
                value={formData.domain_name}
                onChange={(e) => setFormData({ ...formData, domain_name: e.target.value })}
                placeholder="https://yourbusiness.com"
                data-testid="input-domain-name"
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave empty to use <strong>relative paths</strong> — useful for testing the ZIP locally on your computer. Set it once you know the final domain; all internal links in exports (ZIP, WordPress, Cloudflare, Bunny) will then be absolute URLs rooted at that domain. <code>https://</code> is added automatically if omitted.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Phone *</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 555-5555" data-testid="input-phone" /></div>
              <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="info@example.com" data-testid="input-email" /></div>
            </div>
            <div>
              <Label>WhatsApp Number <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
              <Input value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} placeholder="+33 6 50 33 58 19" data-testid="input-whatsapp" />
              <p className="text-xs text-slate-500 mt-1">Include country code. A floating WhatsApp button will appear on all pages.</p>
            </div>
            <div>
              <Label>Google Maps Embed URL <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
              <Input value={formData.google_maps_embed} onChange={(e) => setFormData({ ...formData, google_maps_embed: e.target.value })} placeholder="https://www.google.com/maps/embed?pb=..." data-testid="input-google-maps" />
              <p className="text-xs text-slate-500 mt-1">Paste the embed URL from Google Maps (Share &gt; Embed a map). Shows on homepage, contact &amp; city pages.</p>
            </div>
            <div><Label>Street Address *</Label><Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="123 Main Street" data-testid="input-address" /></div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>City *</Label><Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Chicago" data-testid="input-city" /></div>
              <div><Label>State *</Label><Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="IL" data-testid="input-state" /></div>
              <div><Label>ZIP Code *</Label><Input value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} placeholder="60601" data-testid="input-zip" /></div>
            </div>
            <div><Label>Tagline *</Label><Input value={formData.tagline} onChange={(e) => setFormData({ ...formData, tagline: e.target.value })} placeholder="Your Trusted Local Plumbing Experts" data-testid="input-tagline" /></div>
            <div><Label>Description *</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Professional plumbing services for residential and commercial properties..." rows={3} data-testid="input-description" /></div>
            <div>
              <Label>Footer Disclaimer <span className="text-slate-400 text-xs font-normal">(optional — for affiliate networks)</span></Label>
              <Textarea value={formData.disclaimer} onChange={(e) => setFormData({ ...formData, disclaimer: e.target.value })} placeholder="Disclaimer: [This site] is a free service to assist homeowners in connecting with local service providers..." rows={3} data-testid="input-disclaimer" />
              <p className="text-xs text-slate-500 mt-1">Appears at the bottom of every page. Used by affiliate networks that require disclaimers.</p>
            </div>
            <div>
              <Label>Business Logo (PNG recommended)</Label>
              <div className="mt-1 flex items-start gap-4">
                {logoPreview ? (
                  <div className="relative">
                    <img src={logoPreview} alt="Logo preview" className="h-16 w-auto max-w-[200px] object-contain rounded-lg border border-slate-200 bg-white p-2" data-testid="logo-preview-wizard" />
                    <button onClick={() => { setLogoFile(null); setLogoPreview(null); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600" data-testid="logo-remove-btn"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors" data-testid="logo-upload-label">
                    <Upload className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-600">Upload Logo</span>
                    <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange} className="hidden" data-testid="logo-upload-input" />
                  </label>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">Will be converted to WEBP for optimal performance</p>
            </div>
            <div>
              <Label>Favicon <span className="text-slate-400 text-xs font-normal">(ICO, PNG, or SVG — 32x32 recommended)</span></Label>
              <div className="mt-1 flex items-start gap-4">
                {faviconPreview ? (
                  <div className="relative">
                    <img src={faviconPreview} alt="Favicon preview" className="h-8 w-8 object-contain rounded border border-slate-200 bg-white p-0.5" data-testid="favicon-preview-wizard" />
                    <button onClick={() => { setFaviconFile(null); setFaviconPreview(null); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600" data-testid="favicon-remove-btn"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-1.5 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors" data-testid="favicon-upload-label">
                    <Upload className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-xs text-slate-600">Upload Favicon</span>
                    <input type="file" accept="image/x-icon,image/png,image/svg+xml,.ico,.svg" className="hidden" data-testid="favicon-upload-input" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) { setFaviconFile(file); setFaviconPreview(URL.createObjectURL(file)); }
                    }} />
                  </label>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">Small icon shown in browser tabs</p>
            </div>
            <div>
              <Label>Page Images <span className="text-slate-400 text-xs font-normal">(optional — override stock photos)</span></Label>
              <p className="text-xs text-slate-500 mb-3">Upload your own photos for service pages. If not uploaded, professional stock photos are used automatically.</p>
              <div className="grid gap-3">
                {(formData.services || []).slice(0, 6).map((svc, i) => {
                  const slot = `service_${i}`;
                  return (
                    <div key={slot} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`image-slot-${slot}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{svc.name || `Service ${i+1}`}</p>
                        <p className="text-xs text-slate-400">{pageImagePreviews[slot] ? 'Custom image' : 'Auto (stock photo)'}</p>
                      </div>
                      {pageImagePreviews[slot] ? (
                        <div className="flex items-center gap-2">
                          <img src={pageImagePreviews[slot]} alt="" className="h-10 w-16 object-cover rounded border border-slate-200" />
                          <button onClick={() => { const ni = {...pageImages}; delete ni[slot]; setPageImages(ni); const np = {...pageImagePreviews}; delete np[slot]; setPageImagePreviews(np); }} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-slate-300 rounded cursor-pointer hover:border-orange-400 hover:bg-orange-50 text-xs text-slate-600 transition-colors">
                          <Upload className="w-3 h-3" />Upload
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setPageImages(prev => ({...prev, [slot]: file}));
                              setPageImagePreviews(prev => ({...prev, [slot]: URL.createObjectURL(file)}));
                            }
                          }} />
                        </label>
                      )}
                    </div>
                  );
                })}
                {(!formData.services || formData.services.length === 0) && (
                  <p className="text-xs text-slate-400 italic">Add services in Step 3 first — then you can upload images for each service page here.</p>
                )}
              </div>
            </div>
            <div>
              <Label>Brand Colors</Label>
              <div className="flex items-center gap-6 mt-2">
                <div className="flex items-center gap-3" data-testid="color-picker-primary">
                  <label className="relative w-10 h-10 rounded-lg border-2 border-slate-200 cursor-pointer overflow-hidden shadow-sm hover:shadow-md transition-shadow" style={{ backgroundColor: formData.primary_color }}>
                    <input type="color" value={formData.primary_color} onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                  </label>
                  <div><p className="text-sm font-medium text-slate-700">Primary</p><p className="text-xs text-slate-400 font-mono">{formData.primary_color}</p></div>
                </div>
                <div className="flex items-center gap-3" data-testid="color-picker-secondary">
                  <label className="relative w-10 h-10 rounded-lg border-2 border-slate-200 cursor-pointer overflow-hidden shadow-sm hover:shadow-md transition-shadow" style={{ backgroundColor: formData.secondary_color }}>
                    <input type="color" value={formData.secondary_color} onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                  </label>
                  <div><p className="text-sm font-medium text-slate-700">Secondary</p><p className="text-xs text-slate-400 font-mono">{formData.secondary_color}</p></div>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">Primary is used for buttons and accents. Secondary is used for headers and dark backgrounds.</p>
            </div>
            <div>
              <Label>Hero Section Layout</Label>
              <div className="mt-2 flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, hero_form: !formData.hero_form })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${formData.hero_form ? 'bg-orange-500' : 'bg-slate-300'}`}
                  data-testid="hero-form-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.hero_form ? 'translate-x-5' : ''}`} />
                </button>
                <div>
                  <p className="text-sm font-medium text-slate-700">Contact form in hero section</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formData.hero_form
                      ? "The hero displays a contact form next to the headline on desktop. Visitors can submit without scrolling."
                      : "The hero displays a full-width image next to the headline. Visitors click a button to reach the contact form."}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label>Near Me Page</Label>
              <div className="mt-2 flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, near_me_page: !formData.near_me_page })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${formData.near_me_page ? 'bg-orange-500' : 'bg-slate-300'}`}
                  data-testid="near-me-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.near_me_page ? 'translate-x-5' : ''}`} />
                </button>
                <div>
                  <p className="text-sm font-medium text-slate-700">Generate "Near Me" page</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formData.near_me_page
                      ? "A dedicated SEO page targeting '[service] near me' searches. Includes service areas, local trust signals, and LocalBusiness schema."
                      : "No near me page will be generated."}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label>Blog Section</Label>
              <div className="mt-2 flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, blog_enabled: !formData.blog_enabled })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${formData.blog_enabled ? 'bg-orange-500' : 'bg-slate-300'}`}
                  data-testid="blog-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.blog_enabled ? 'translate-x-5' : ''}`} />
                </button>
                <div>
                  <p className="text-sm font-medium text-slate-700">Enable blog / articles</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formData.blog_enabled
                      ? "A blog hub page and individual article pages will be generated. You can add AI-generated articles from the project detail page after creation."
                      : "No blog pages will be generated."}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t pt-4 mt-2">
              <Label>Tracking & Scripts <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
              <p className="text-xs text-slate-500 mb-3">Paste any tracking codes, verification tags, or custom scripts. They will be injected into every generated page.</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-600">Head Scripts <span className="text-slate-400 font-normal">(injected before &lt;/head&gt;)</span></Label>
                  <Textarea
                    value={formData.head_scripts}
                    onChange={(e) => setFormData({ ...formData, head_scripts: e.target.value })}
                    placeholder={"<!-- Google Analytics -->\n<script async src=\"https://www.googletagmanager.com/gtag/js?id=G-XXXXXXX\"></script>\n\n<!-- Google Search Console -->\n<meta name=\"google-site-verification\" content=\"...\" />"}
                    rows={4}
                    className="font-mono text-xs"
                    data-testid="input-head-scripts"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Body Scripts <span className="text-slate-400 font-normal">(injected before &lt;/body&gt;)</span></Label>
                  <Textarea
                    value={formData.body_scripts}
                    onChange={(e) => setFormData({ ...formData, body_scripts: e.target.value })}
                    placeholder={"<!-- Facebook Pixel -->\n<script>!function(f,b,e,v,n,t,s){...}('init','YOUR_PIXEL_ID');</script>\n\n<!-- Google Tag Manager (noscript) -->\n<noscript>...</noscript>"}
                    rows={4}
                    className="font-mono text-xs"
                    data-testid="input-body-scripts"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={() => setStep(1)}><ChevronLeft className="w-4 h-4 mr-2" /> Back</Button>
            <Button variant="ghost" onClick={saveDraft} disabled={isSavingDraft} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" /> {isSavingDraft ? "Saving..." : "Save as Draft"}
            </Button>
            <Button onClick={() => setStep(3)} disabled={!formData.name || !formData.business_name || !formData.phone || !formData.email} className="bg-orange-500 hover:bg-orange-600">Next <ChevronRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </div>
      )}

      {/* Step 3: Services */}
      {step === 3 && (
        <div className="space-y-6 max-w-3xl">
          <h3 className="text-lg font-semibold">Services</h3>
          <p className="text-slate-600">Add the services your business offers. Each service will get its own page.</p>
          <div className="space-y-3">
            {formData.services.map((service, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div><p className="font-medium">{service.name}</p><p className="text-sm text-slate-500">{service.description}</p></div>
                <Button variant="ghost" size="sm" onClick={() => removeService(index)} className="text-red-500 hover:text-red-600"><X className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
          <Card className="border-dashed">
            <CardHeader><CardTitle className="text-base">Add New Service</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Service Name *</Label><Input value={newService.name} onChange={(e) => setNewService({ ...newService, name: e.target.value })} placeholder="Drain Cleaning" /></div>
                <div><Label>URL Slug</Label><Input value={newService.slug} onChange={(e) => setNewService({ ...newService, slug: e.target.value })} placeholder="drain-cleaning" /></div>
              </div>
              <div><Label>Description</Label><Textarea value={newService.description} onChange={(e) => setNewService({ ...newService, description: e.target.value })} placeholder="Professional drain cleaning services..." rows={2} /></div>
              <Button onClick={addService} variant="outline" className="w-full"><Plus className="w-4 h-4 mr-2" /> Add Service</Button>
            </CardContent>
          </Card>
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={() => setStep(2)}><ChevronLeft className="w-4 h-4 mr-2" /> Back</Button>
            <Button variant="ghost" onClick={saveDraft} disabled={isSavingDraft} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" /> {isSavingDraft ? "Saving..." : "Save as Draft"}
            </Button>
            <Button onClick={() => setStep(4)} disabled={formData.services.length === 0} className="bg-orange-500 hover:bg-orange-600">Next <ChevronRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </div>
      )}

      {/* Step 4: Locations */}
      {step === 4 && (
        <div className="space-y-6 max-w-3xl">
          <h3 className="text-lg font-semibold">Service Locations</h3>
          <p className="text-slate-600">Add cities where you provide services. Each city will get its own landing page.</p>
          <div className="space-y-3">
            {formData.cities.map((city, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">{city.name}, {city.state}{city.suffix ? ` — ${city.suffix}` : ''}</p>
                  <p className="text-sm text-slate-500">/{city.slug}.html</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeCity(index)} className="text-red-500 hover:text-red-600"><X className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
          <Card className="border-dashed">
            <CardHeader><CardTitle className="text-base">Add New Location</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-4 gap-4">
                <div><Label>City Name *</Label><Input value={newCity.name} onChange={(e) => setNewCity({ ...newCity, name: e.target.value })} placeholder="Chicago" data-testid="city-name-input" /></div>
                <div><Label>State *</Label><Input value={newCity.state} onChange={(e) => setNewCity({ ...newCity, state: e.target.value })} placeholder="IL" data-testid="city-state-input" /></div>
                <div><Label>Suffix <span className="text-slate-400 text-xs font-normal">(optional)</span></Label><Input value={newCity.suffix} onChange={(e) => setNewCity({ ...newCity, suffix: e.target.value })} placeholder="60601" data-testid="city-suffix-input" /></div>
                <div><Label>URL Slug <span className="text-slate-400 text-xs font-normal">(auto)</span></Label><Input value={generateSlug([newCity.name, newCity.state, newCity.suffix].filter(Boolean).join(' '))} readOnly className="bg-slate-100 text-slate-500 cursor-default" data-testid="city-slug-preview" /></div>
              </div>
              <Button onClick={addCity} variant="outline" className="w-full"><Plus className="w-4 h-4 mr-2" /> Add Location</Button>
            </CardContent>
          </Card>
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={() => setStep(3)}><ChevronLeft className="w-4 h-4 mr-2" /> Back</Button>
            <Button variant="ghost" onClick={saveDraft} disabled={isSavingDraft} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" /> {isSavingDraft ? "Saving..." : "Save as Draft"}
            </Button>
            <Button onClick={() => setStep(5)} className="bg-orange-500 hover:bg-orange-600">Next <ChevronRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </div>
      )}

      {/* Step 5: Testimonials */}
      {step === 5 && (
        <div className="space-y-6 max-w-3xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Customer Testimonials</h3>
              <p className="text-sm text-slate-500 mt-1">Add reviews to build trust. Optional — skip if you don't have any yet.</p>
            </div>
            <Badge variant="outline" className="text-slate-500">{formData.testimonials.length} added</Badge>
          </div>
          {formData.testimonials.map((testimonial, idx) => (
            <Card key={idx} data-testid={`testimonial-card-${idx}`}>
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Testimonial #{idx + 1}</span>
                  <Button variant="ghost" size="sm" onClick={() => { const updated = formData.testimonials.filter((_, i) => i !== idx); setFormData({ ...formData, testimonials: updated }); }} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0" data-testid={`testimonial-remove-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label>Name *</Label><Input value={testimonial.name} onChange={(e) => { const updated = [...formData.testimonials]; updated[idx] = { ...updated[idx], name: e.target.value }; setFormData({ ...formData, testimonials: updated }); }} placeholder="Jean Dupont" data-testid={`testimonial-name-${idx}`} /></div>
                  <div><Label>Location</Label><Input value={testimonial.location} onChange={(e) => { const updated = [...formData.testimonials]; updated[idx] = { ...updated[idx], location: e.target.value }; setFormData({ ...formData, testimonials: updated }); }} placeholder="Lyon, France" data-testid={`testimonial-location-${idx}`} /></div>
                </div>
                <div>
                  <Label>Rating</Label>
                  <div className="flex items-center gap-1 mt-1" data-testid={`testimonial-rating-${idx}`}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button key={star} type="button" onClick={() => { const updated = [...formData.testimonials]; updated[idx] = { ...updated[idx], rating: star }; setFormData({ ...formData, testimonials: updated }); }} className="p-0.5 transition-colors">
                        <Star className={`w-5 h-5 ${star <= testimonial.rating ? "fill-yellow-400 text-yellow-400" : "fill-none text-slate-300"}`} />
                      </button>
                    ))}
                    <span className="text-sm text-slate-500 ml-2">{testimonial.rating}/5</span>
                  </div>
                </div>
                <div><Label>Review Text *</Label><Textarea value={testimonial.text} onChange={(e) => { const updated = [...formData.testimonials]; updated[idx] = { ...updated[idx], text: e.target.value }; setFormData({ ...formData, testimonials: updated }); }} placeholder="Great service! They were professional, on time, and did excellent work..." rows={3} data-testid={`testimonial-text-${idx}`} /></div>
              </CardContent>
            </Card>
          ))}
          <Button variant="outline" onClick={() => { setFormData({ ...formData, testimonials: [...formData.testimonials, { name: "", location: "", rating: 5, text: "" }] }); }} className="w-full border-dashed" data-testid="add-testimonial-btn"><Plus className="w-4 h-4 mr-2" /> Add Testimonial</Button>
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={() => setStep(4)}><ChevronLeft className="w-4 h-4 mr-2" /> Back</Button>
            <Button variant="ghost" onClick={saveDraft} disabled={isSavingDraft} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" /> {isSavingDraft ? "Saving..." : "Save as Draft"}
            </Button>
            <Button onClick={() => setStep(6)} className="bg-orange-500 hover:bg-orange-600">Next <ChevronRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </div>
      )}

      {/* Step 6: Review */}
      {step === 6 && (
        <div className="space-y-6 max-w-3xl">
          <h3 className="text-lg font-semibold">Review & Create</h3>
          <Card>
            <CardHeader><CardTitle>Project Summary</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><p className="text-sm text-slate-500">Project Name</p><p className="font-medium">{formData.name}</p></div>
                <div><p className="text-sm text-slate-500">Template</p><p className="font-medium capitalize">{formData.template}</p></div>
                <div><p className="text-sm text-slate-500">Business Name</p><p className="font-medium">{formData.business_name}</p></div>
                <div><p className="text-sm text-slate-500">Domain</p><p className="font-medium break-all">{formData.domain_name || <span className="text-slate-400 italic">Not set (relative paths)</span>}</p></div>
                <div><p className="text-sm text-slate-500">Phone</p><p className="font-medium">{formData.phone}</p></div>
                <div><p className="text-sm text-slate-500">Location</p><p className="font-medium">{formData.city}, {formData.state}</p></div>
                <div><p className="text-sm text-slate-500">Email</p><p className="font-medium">{formData.email}</p></div>
                {formData.whatsapp && <div><p className="text-sm text-slate-500">WhatsApp</p><p className="font-medium">{formData.whatsapp}</p></div>}
                {formData.google_maps_embed && <div><p className="text-sm text-slate-500">Google Maps</p><p className="font-medium text-green-600">Enabled</p></div>}
                {formData.disclaimer && <div><p className="text-sm text-slate-500">Disclaimer</p><p className="font-medium text-green-600">Enabled</p></div>}
                <div><p className="text-sm text-slate-500">Near Me Page</p><p className="font-medium">{formData.near_me_page ? "Enabled" : "Disabled"}</p></div>
                <div><p className="text-sm text-slate-500">Blog</p><p className="font-medium">{formData.blog_enabled ? "Enabled" : "Disabled"}</p></div>
                {(formData.head_scripts || formData.body_scripts) && <div><p className="text-sm text-slate-500">Tracking Scripts</p><p className="font-medium text-green-600">{[formData.head_scripts && "Head", formData.body_scripts && "Body"].filter(Boolean).join(" + ")}</p></div>}
              </div>
              {logoPreview && (<div className="border-t pt-4"><p className="text-sm text-slate-500 mb-2">Logo</p><img src={logoPreview} alt="Logo" className="h-12 w-auto object-contain" data-testid="logo-preview-review" /></div>)}
              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-2">Brand Colors</p>
                <div className="flex items-center gap-4" data-testid="color-preview-review">
                  <div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full border border-slate-200" style={{ backgroundColor: formData.primary_color }} /><span className="text-sm">Primary <span className="font-mono text-slate-400">{formData.primary_color}</span></span></div>
                  <div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full border border-slate-200" style={{ backgroundColor: formData.secondary_color }} /><span className="text-sm">Secondary <span className="font-mono text-slate-400">{formData.secondary_color}</span></span></div>
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-2">Hero Layout</p>
                <Badge variant={formData.hero_form ? "default" : "outline"}>
                  {formData.hero_form ? "Contact form in hero" : "Image in hero"}
                </Badge>
              </div>
              <div className="border-t pt-4"><p className="text-sm text-slate-500 mb-2">Services ({formData.services.length})</p><div className="flex flex-wrap gap-2">{formData.services.map((s, i) => (<Badge key={i} variant="secondary">{s.name}</Badge>))}</div></div>
              <div className="border-t pt-4"><p className="text-sm text-slate-500 mb-2">Service Areas ({formData.cities.length})</p><div className="flex flex-wrap gap-2">{formData.cities.map((c, i) => (<Badge key={i} variant="outline">{c.name}, {c.state}</Badge>))}</div></div>
              {formData.testimonials.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-500 mb-2">Testimonials ({formData.testimonials.length})</p>
                  <div className="space-y-2">
                    {formData.testimonials.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <div className="flex">{[...Array(t.rating)].map((_, j) => (<Star key={j} className="w-3 h-3 fill-yellow-400 text-yellow-400" />))}</div>
                        <span className="font-medium">{t.name}</span>
                        {t.location && <span className="text-slate-400">— {t.location}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-2">Pages to be generated</p>
                <ul className="text-sm space-y-1">
                  <li>• index.html (Home page)</li><li>• about.html (About page)</li><li>• contact.html (Contact page)</li><li>• privacy-policy.html (Privacy policy)</li>
                  <li>• {formData.services.length} service pages</li><li>• {formData.cities.length} city pages</li><li>• {formData.services.length * formData.cities.length} city+service pages</li>
                  {formData.blog_enabled && <li>• blog.html (Blog hub page)</li>}
                </ul>
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={() => setStep(5)}><ChevronLeft className="w-4 h-4 mr-2" /> Back</Button>
            <Button variant="ghost" onClick={saveDraft} disabled={isSavingDraft} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" /> {isSavingDraft ? "Saving..." : "Save as Draft"}
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-orange-500 hover:bg-orange-600">{isSubmitting ? "Creating..." : draftId ? "Publish Project" : "Create Project"}</Button>
          </div>
        </div>
      )}
    </div>
  );
};
